import { useEffect, useRef, useState } from 'react';
import { validatePassword } from 'features/Auth/utils/password.utils';
import { updateFusionPassword } from 'services/account/account.service';

const EMPTY_PASSWORDS = { currentPassword: '', newPassword: '', confirmPassword: '' };
const SAVE_ERROR = 'Unable to change your password. Please try again.';

const getValidationError = ({ currentPassword, newPassword, confirmPassword }) => {
  if (!currentPassword) return 'Original password is required';
  if (newPassword !== confirmPassword) return 'Passwords do not match';
  if (newPassword === currentPassword) {
    return 'New password cannot be the same as the current password';
  }

  const validation = validatePassword(newPassword);
  return validation.isValid ? null : validation.message;
};

/** Owns password drafts, validation, and the change-password request. */
const useChangePassword = () => {
  const [passwords, setPasswords] = useState(EMPTY_PASSWORDS);
  const [submitError, setSubmitError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSucceeded, setHasSucceeded] = useState(false);
  const isMountedRef = useRef(false);
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const updatePassword = (field, value) => {
    setPasswords((current) => ({ ...current, [field]: value }));
    setSubmitError(null);
  };

  const submit = async (event) => {
    event.preventDefault();
    if (isSubmittingRef.current) return;

    const validationError = getValidationError(passwords);
    if (validationError) {
      setSubmitError(validationError);
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await updateFusionPassword(passwords.currentPassword, passwords.newPassword);
      if (!isMountedRef.current) return;

      // The existing service returns undefined when its request fails.
      const isSuccessful = response?.status >= 200 && response?.status < 300;
      if (!isSuccessful) {
        setSubmitError(SAVE_ERROR);
        return;
      }

      setPasswords(EMPTY_PASSWORDS);
      setHasSucceeded(true);
    } catch (error) {
      if (!isMountedRef.current) return;
      const message = error?.response?.data?.message;
      setSubmitError(
        typeof message === 'string' ? `Error changing password: ${message}` : SAVE_ERROR,
      );
    } finally {
      isSubmittingRef.current = false;
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  const validationError = Object.values(passwords).every(Boolean)
    ? getValidationError(passwords)
    : null;

  return {
    passwords,
    updatePassword,
    submit,
    error: submitError || validationError,
    isSubmitting,
    hasSucceeded,
  };
};

export default useChangePassword;
